import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { NgModule } from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import {
    ExtraOptions,
    PreloadAllModules,
    RouterModule,
    Routes,
} from "@angular/router";
import { MarkdownModule } from "ngx-markdown";
import { FuseModule } from "@fuse";
import { FuseConfigModule } from "@fuse/services/config";
import { FuseMockApiModule } from "@fuse/lib/mock-api";
import { CoreModule } from "app/fuse-core/core.module";
import { appConfig } from "app/fuse-core/config/app.config";
import { mockApiServices } from "app/mock-api";
import { LayoutModule } from "app/layout/layout.module";
import { FormioModule } from "@formio/angular";
import { TranslateModule } from "@ngx-translate/core";
import { AppComponent } from "app/app.component";

import { StoreModule } from "@ngrx/store";
import { LoadingSpinnerComponent } from "./loading-spinner/loading-spinner.component";
import { environment } from "environments/environment";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import * as fromApp from "./core/store/reducers/app.reducer";
import { SpinnerInterceptor } from "./core/interceptors/spinner.interceptor";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import {
    MatSnackBarModule,
    MAT_SNACK_BAR_DEFAULT_OPTIONS,
} from "@angular/material/snack-bar";
import {
    MatDialogModule,
    MAT_DIALOG_DEFAULT_OPTIONS,
} from "@angular/material/dialog";
import { MatStepperModule } from "@angular/material/stepper";
import { MatExpansionModule } from "@angular/material/expansion";
import { SharedModule } from "./components/shared/shared.module";
import { ToastrModule } from "ngx-toastr";

import {
    FaIconLibrary,
    FontAwesomeModule,
} from "@fortawesome/angular-fontawesome";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { LoginComponent } from "./components/auth/login/login.component";
import { TokenInterceptor } from "./core/interceptors/token.interceptor";
import { AuthGuard } from "./core/guards/auth.guard";
import { LayoutComponent } from "./layout/layout.component";
import { InitialDataResolver } from "./app.resolvers";
import { appRoutes } from "./app.routing";
import { DatePipe } from "@angular/common";

import {
    IPublicClientApplication,
    PublicClientApplication,
    InteractionType,
} from "@azure/msal-browser";
import {
    MsalGuard,
    MsalInterceptor,
    MsalBroadcastService,
    MsalInterceptorConfiguration,
    MsalModule,
    MsalService,
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalGuardConfiguration,
    MsalRedirectComponent,
} from "@azure/msal-angular";
import {
    msalConfig,
    loginRequest,
    protectedResources,
} from "./modules/auth/auth-config";

const routes: Routes = [
    // ...appRoutes,
    {
        path: "project",
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            layout: "modern",
        },
        loadChildren: () =>
            import("./components/home/home.module").then((x) => x.HomeModule),
        canActivate: [AuthGuard],
    },
    {
        path: "auth",
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            layout: "empty",
        },
        loadChildren: () =>
            import("./components/auth/auth.module").then((x) => x.AuthModule),
    },
    {
        path: "forms",
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            layout: "modern",
        },
        loadChildren: () =>
            import("./components/forms/forms.module").then(
                (x) => x.FormsModule
            ),
        canActivate: [AuthGuard],
    },
    {
        path: "users",
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            layout: "modern",
        },
        loadChildren: () =>
            import("./components/users/users.module").then(
                (x) => x.UsersModule
            ),
        canActivate: [AuthGuard],
    },
    {
        path: "lookups",
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            layout: "modern",
        },
        loadChildren: () =>
            import("./components/lookups/lookups.module").then(
                (x) => x.LookupsModule
            ),
        canActivate: [AuthGuard],
    },
    {
      path: "delegations",
      component: LayoutComponent,
      resolve: {
          initialData: InitialDataResolver,
      },
      data: {
          layout: "modern",
      },
      loadChildren: () =>
          import("./components/delegation/delegation.module").then(
              (x) => x.DelegationModule
          ),
      canActivate: [AuthGuard],
    },
    {
        path: "",
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            layout: "modern",
        },
        loadChildren: () =>
            import("./components/requests/requests.module").then(
                (x) => x.RequestsModule
            ),
        canActivate: [AuthGuard],
    },
    {
        path: "**",
        redirectTo: "project/all/home",
    },
];

const routerConfig: ExtraOptions = {
    scrollPositionRestoration: "enabled",
    preloadingStrategy: PreloadAllModules,
};

/**
 * Here we pass the configuration parameters to create an MSAL instance.
 * For more info, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/configuration.md
 */

export function MSALInstanceFactory(): IPublicClientApplication {
    return new PublicClientApplication(msalConfig);
}

/**
 * MSAL Angular will automatically retrieve tokens for resources
 * added to protectedResourceMap. For more info, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/initialization.md#get-tokens-for-web-api-calls
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, Array<string>>();

    protectedResourceMap.set(
        protectedResources.eFormsAPI.endpoint,
        protectedResources.eFormsAPI.scopes
    );

    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap,
    };
}

/**
 * Set your default interaction type for MSALGuard here. If you have any
 * additional scopes you want the user to consent upon login, add them here as well.
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return {
        interactionType: InteractionType.Redirect,
        authRequest: loginRequest,
    };
}

@NgModule({
    declarations: [AppComponent, LoadingSpinnerComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        HttpClientModule,
        RouterModule.forRoot(routes, routerConfig),

        TranslateModule.forRoot(),

        // Fuse & Fuse Mock API
        FuseModule,
        FuseConfigModule.forRoot(appConfig),
        FuseMockApiModule.forRoot(mockApiServices),

        // Core
        CoreModule,

        // Layout
        LayoutModule,

        // Material
        MatIconModule,
        MatProgressSpinnerModule,
        MatSnackBarModule,
        MatDialogModule,
        MatExpansionModule,
        MatStepperModule,
        MatFormFieldModule,
        MatInputModule,

        // Formio
        FormioModule,

        // Toastr
        ToastrModule.forRoot(),

        // 3rd party modules
        MarkdownModule.forRoot({}),

        // Store Modules
        StoreModule.forRoot(fromApp.reducer),
        StoreDevtoolsModule.instrument({
            maxAge: 25,
            logOnly: environment.production,
        }),
        FontAwesomeModule,
        MsalModule,
    ],
    providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: SpinnerInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true,
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory,
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory,
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory,
        },
        { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
        {
            provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
            useValue: { duration: 10000 },
        },
        {
            provide: MAT_DIALOG_DEFAULT_OPTIONS,
            useValue: { hasBackdrop: true },
        },
        DatePipe,
        MsalService,
        MsalGuard,
        MsalBroadcastService,
    ],
    bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {
    // constructor(library: FaIconLibrary) {
    //     library.addIconPacks(fas, far);
    // }

    constructor() {}
}
